import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Toaster } from "./ui/sonner";
import { MarketingFooter, MarketingHeader } from "./MarketingChrome";
import { LogicielShowcaseCard } from "./logiciels/LogicielShowcaseCard";

const heroMotion = {
  hidden: { opacity: 0, y: 26 },
  show: (index = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: index * 0.08, ease: "easeOut" },
  }),
};

const softwareLogos = {
  totalCycle: "https://customer-assets.emergentagent.com/job_f245e132-150b-471b-b64b-e2b58de02af0/artifacts/kgvibmp5_ChatGPT%20Image%20May%2014%2C%202026%2C%2008_12_04%20PM%20%281%29.png",
  planning: "https://customer-assets.emergentagent.com/job_f245e132-150b-471b-b64b-e2b58de02af0/artifacts/31itu1qf_ChatGPT%20Image%20May%2014%2C%202026%2C%2008_12_05%20PM%20%282%29.png",
  ifcToDpgf: "https://customer-assets.emergentagent.com/job_f245e132-150b-471b-b64b-e2b58de02af0/artifacts/ldinpxf0_ChatGPT%20Image%20May%2014%2C%202026%2C%2008_12_05%20PM%20%283%29.png",
  ifcToDpgfLight: "https://customer-assets.emergentagent.com/job_f245e132-150b-471b-b64b-e2b58de02af0/artifacts/qnphkokq_ChatGPT%20Image%20May%2014%2C%202026%2C%2008_12_05%20PM%20%284%29.png",
};

export default function LogicielsPage() {
  const openDemoEmail = (subject) => () => {
    window.location.href = `mailto:jad@bimcubes.fr?subject=${encodeURIComponent(subject)}`;
  };

  const showSoonToast = () => {
    toast.info("Cette solution sera bientôt disponible. Nous pouvons vous informer dès son lancement.");
  };

  const logiciels = [
    {
      slug: "bimcubes-total-cycle",
      name: "Bimcubes Total Cycle",
      badge: "Sur demande",
      description: "Bimcubes Total Cycle est une plateforme digitale de gestion complète du cycle de vie des actifs immobiliers et de construction, pensée pour piloter patrimoine, travaux, documents, coûts et indicateurs depuis une interface unique.",
      price: "À partir de 50 € HT / utilisateur / mois",
      logoUrl: softwareLogos.totalCycle,
      logoAlt: "Logo Bimcubes Total Cycle",
      features: [
        "Gestion centralisée des actifs immobiliers",
        "Suivi des bâtiments, hôtels, villas et commerces",
        "Carte interactive des actifs",
        "Suivi CAPEX, OPEX et revenus",
        "Gestion des documents juridiques et contrats",
        "Suivi BOQ / DPGF",
        "Suivi d’avancement des travaux",
        "Gestion des variations et paiements",
        "Tableaux de bord financiers",
        "Suivi maintenance et performance",
        "Indicateurs visuels pour décisions rapides",
        "Logique BIM, BOQ et carbone intégrée",
        "Accès distant pour familles, holdings et gestionnaires",
      ],
      ctaLabel: "Demander une démo",
      ctaTone: "primary",
      onAction: openDemoEmail("Demande de démonstration — Bimcubes Total Cycle"),
    },
    {
      slug: "bimcubes-planning",
      name: "Bimcubes Planning",
      badge: "Bientôt disponible",
      description: "Bimcubes Planning est une solution de planification projet en ligne pour créer, organiser et suivre des plannings de chantier ou de projet depuis une interface simple, collaborative et accessible à distance.",
      price: "À partir de 9 € HT / utilisateur / mois",
      logoUrl: softwareLogos.planning,
      logoAlt: "Logo Bimcubes Planning",
      features: [
        "Création de tâches et sous-tâches",
        "Dates de début et de fin",
        "Durées de tâches",
        "Dépendances entre tâches",
        "Vue planning / Gantt",
        "Suivi d’avancement",
        "Collaboration en ligne",
        "Partage avec l’équipe projet",
        "Consultation depuis navigateur",
        "Interface simple et accessible",
        "Gestion des plannings de chantier",
        "Gestion des plannings projet",
      ],
      ctaLabel: "Bientôt disponible",
      ctaTone: "disabled",
      onAction: showSoonToast,
    },
    {
      slug: "bimcubes-ifc-to-dpgf",
      name: "Bimcubes IFC to DPGF",
      badge: "Bientôt disponible",
      description: "Bimcubes IFC to DPGF prépare le passage du modèle BIM vers une structure de chiffrage claire en analysant objets, catégories, quantités et métadonnées pour générer un DPGF structuré et bien alloti.",
      price: "À partir de 6 € HT / utilisateur / mois",
      logoUrl: softwareLogos.ifcToDpgf,
      logoAlt: "Logo Bimcubes IFC to DPGF",
      features: [
        "Upload de fichier IFC",
        "Lecture des objets BIM",
        "Analyse des catégories IFC",
        "Extraction des quantités",
        "Lecture des métadonnées disponibles",
        "Classement automatique par lots",
        "Génération d’un DPGF structuré",
        "Interface de contrôle et correction",
        "Conservation du projet sur la plateforme",
        "Amélioration progressive des mappings",
        "Export des données DPGF",
        "Passage plus rapide du BIM vers le chiffrage",
      ],
      ctaLabel: "Bientôt disponible",
      ctaTone: "disabled",
      onAction: showSoonToast,
    },
    {
      slug: "bimcubes-ifc-to-dpgf-light",
      name: "Bimcubes IFC to DPGF Light",
      badge: "Gratuit",
      description: "Bimcubes IFC to DPGF Light est une version gratuite et instantanée du concept IFC vers DPGF, pensée pour un usage simple, rapide et ponctuel sans conservation des données sur la plateforme.",
      price: "Gratuit",
      logoUrl: softwareLogos.ifcToDpgfLight,
      logoAlt: "Logo Bimcubes IFC to DPGF Light",
      mode: "journey",
      journey: ["Upload IFC", "Génération automatique", "Download Excel", "Aucune conservation des données"],
      ctaLabel: "Bientôt disponible",
      ctaTone: "disabled",
      onAction: showSoonToast,
    },
  ];

  return (
    <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(190,242,100,0.14),_transparent_24%),linear-gradient(180deg,_#fbfdf8_0%,_#f8fcf3_44%,_#ffffff_100%)]" data-testid="logiciels-page">
      <MarketingHeader />

      <main>
        <section className="relative overflow-hidden px-4 pb-10 pt-12 sm:px-6 lg:px-8 lg:pb-16 lg:pt-20" data-testid="logiciels-hero-section">
          <div className="absolute inset-x-0 top-0 h-[30rem] bg-[radial-gradient(circle_at_top,_rgba(45,212,191,0.12),_transparent_42%)]" />
          <div className="absolute left-[8%] top-[20%] h-48 w-48 rounded-full bg-[#d9f99d]/30 blur-3xl" />
          <div className="absolute right-[10%] top-[18%] h-56 w-56 rounded-full bg-[#99f6e4]/25 blur-3xl" />

          <div className="relative mx-auto max-w-7xl text-center">
            <motion.div animate="show" className="mx-auto max-w-5xl" initial="hidden" variants={heroMotion}>
              <div className="mx-auto inline-flex rounded-full border border-[#d8ece4] bg-white/92 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#0f766e] shadow-[0_16px_40px_-32px_rgba(15,118,110,0.5)]" data-testid="logiciels-hero-eyebrow">
                Logiciels métiers Bimcubes — tarifs HT
              </div>
              <h1 className="bim-display mx-auto mt-8 max-w-5xl text-4xl font-semibold leading-[1.02] tracking-tight text-slate-950 sm:text-5xl lg:text-6xl" data-testid="logiciels-hero-title">
                Une vitrine claire pour découvrir les logiciels Bimcubes, leurs usages et leur positionnement produit.
              </h1>
              <p className="mx-auto mt-8 max-w-3xl text-base leading-8 text-slate-600 sm:text-lg" data-testid="logiciels-hero-description">
                Des cartes produit premium, une lecture simple des fonctionnalités et des prix visibles pour comprendre rapidement les prochaines solutions de l’écosystème Bimcubes.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="px-4 py-6 sm:px-6 lg:px-8 lg:py-10" data-testid="logiciels-showcase-section">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="logiciels-showcase-eyebrow">Showcase produit</div>
              <h2 className="bim-display mt-4 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl" data-testid="logiciels-showcase-title">
                Quatre solutions pour relier patrimoine, planning, BIM et chiffrage.
              </h2>
              <p className="mt-5 text-base leading-8 text-slate-600 sm:text-lg" data-testid="logiciels-showcase-description">
                Cliquez sur une carte pour faire glisser son contenu et afficher les fonctionnalités détaillées, le prix HT et le bon appel à l’action.
              </p>
            </div>

            <div className="mt-12 grid gap-8 lg:grid-cols-2" data-testid="logiciels-showcase-grid">
              {logiciels.map((logiciel, index) => (
                <LogicielShowcaseCard index={index} key={logiciel.slug} logiciel={logiciel} />
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 pb-16 pt-10 sm:px-6 lg:px-8 lg:pb-24" data-testid="logiciels-contact-section">
          <div className="mx-auto max-w-5xl overflow-hidden rounded-[2.8rem] border border-[#1d4ed8]/15 bg-[linear-gradient(135deg,_#0f766e_0%,_#0f4c81_50%,_#0f172a_100%)] px-6 py-10 text-white shadow-[0_40px_100px_-58px_rgba(15,76,129,0.5)] sm:px-10 sm:py-12 lg:px-14 lg:py-14">
            <div className="text-center">
              <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#bfdbfe]" data-testid="logiciels-contact-eyebrow">Contact</div>
              <h2 className="bim-display mt-4 text-4xl font-semibold tracking-tight sm:text-5xl" data-testid="logiciels-contact-title">
                Vous souhaitez en savoir plus sur nos logiciels ?
              </h2>
              <p className="mx-auto mt-5 max-w-3xl text-base leading-8 text-slate-100 sm:text-lg" data-testid="logiciels-contact-description">
                Contactez Bimcubes pour demander une démonstration, discuter de votre besoin ou être informé du lancement des prochaines solutions.
              </p>
            </div>

            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row" data-testid="logiciels-contact-actions">
              <Button asChild className="h-12 rounded-full bg-white px-6 text-[#0f4c81] hover:bg-slate-100">
                <a data-testid="logiciels-contact-demo-button" href="mailto:jad@bimcubes.fr?subject=Demande%20de%20démonstration%20logiciels%20Bimcubes">
                  Demander une démo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button asChild className="h-12 rounded-full border-white/25 bg-white/10 px-6 text-white hover:bg-white/15" variant="outline">
                <a data-testid="logiciels-contact-email-button" href="mailto:jad@bimcubes.fr">jad@bimcubes.fr</a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
      <Toaster closeButton position="top-center" />
    </div>
  );
}